import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { LiraRunAdvScript } from '@sbt-web/adv';
import { usePublicUser } from '@sbt-web/auth';
import ErrorBoundary from '@sbt-web/error-boundary';
import { getUserAds, searchShops } from '@sbt-web/network/client';
import type { AdItem } from '@sbt-web/network/types';
import { createSkyscraperSizeConstraints } from '@/client/components/Adv/Lira/createSkyscraperSizeConstraints';
import { getDesktopFallbackConfig, getMsiteFallbackConfig } from '@/client/components/Adv/Lira/fallbackConfig/listing';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { unwrap } from '@/shared/helpers/Promise';
import { ListingTargetingProps, targetingForListing } from '../targeting/forListing';
import { useGetVerticalByCategoryId } from './useGetVerticalByCategoryId';
const adtest = process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT === 'prod' ? 'off' : 'on';
type Props = ListingTargetingProps & {
  isModalOpened: boolean;
  adItems: AdItem[];
};
const LiraWrapper = t0 => {
  const $ = _c(38);
  const {
    category,
    adType,
    query,
    geo,
    radiusSearch,
    pageIndex,
    filtersLabels,
    includeShippableOnly,
    adItems,
    isModalOpened
  } = t0;
  const [clientSideNavigationStarted, setClientSideNavigationStarted] = useState(false);
  const previousItemsList = useRef(null);
  const isFirstRender = useRef(true);
  const vertical = useGetVerticalByCategoryId(category.id);
  let t1;
  if ($[0] !== vertical) {
    t1 = getDesktopFallbackConfig(vertical);
    $[0] = vertical;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fallbackConfigDesktop = t1;
  let t2;
  if ($[2] !== vertical) {
    t2 = getMsiteFallbackConfig(vertical);
    $[2] = vertical;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fallbackConfigMsite = t2;
  const queryStringForAdsense = query ?? category.label;
  const user = usePublicUser();
  const [userActiveAd, setUserActiveAd] = useState(false);
  const [isPro, setIsPro] = useState(false);
  let t3;
  let t4;
  if ($[4] !== user) {
    t3 = () => {
      const fetchUserInfoForTargeting = async () => {
        if (user) {
          const [getSearchShopResponse, getUserAdsResponse] = await Promise.allSettled([searchShops(process.env.HEPHAESTUS_BASE_URL as string, {
            uid: user.id
          }), getUserAds(`${process.env.NEXT_PUBLIC_ENV_BASE_URL}/hades`, user.id)]);
          const userAdsResponse = unwrap(getUserAdsResponse)?.body;
          const shopsResponse = unwrap(getSearchShopResponse)?.body;
          if (userAdsResponse && userAdsResponse.total > 0) {
            const activeAds = userAdsResponse?.ads.filter(_temp);
            setUserActiveAd(activeAds.length > 0);
          }
          setIsPro(!!shopsResponse?.shops?.[0]);
        }
      };
      fetchUserInfoForTargeting();
    };
    t4 = [user];
    $[4] = user;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== adType || $[8] !== category || $[9] !== filtersLabels || $[10] !== geo || $[11] !== includeShippableOnly || $[12] !== isPro || $[13] !== pageIndex || $[14] !== query || $[15] !== radiusSearch || $[16] !== userActiveAd) {
    t5 = targetingForListing({
      category,
      geo,
      query,
      adType,
      pageIndex,
      radiusSearch,
      filtersLabels,
      includeShippableOnly,
      isPro,
      userActiveAd
    });
    $[7] = adType;
    $[8] = category;
    $[9] = filtersLabels;
    $[10] = geo;
    $[11] = includeShippableOnly;
    $[12] = isPro;
    $[13] = pageIndex;
    $[14] = query;
    $[15] = radiusSearch;
    $[16] = userActiveAd;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const targeting = t5;
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createSkyscraperSizeConstraints(LiraContainerIds.listing.skyscraperLeft, LiraContainerIds.listing.skyscraperRight);
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      isLazyLoad: true
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      isLazyLoad: true
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      ...t6,
      [LiraContainerIds.listing.interstitialLazy]: t7,
      [LiraContainerIds.listing.inContentBottom]: t8,
      [LiraContainerIds.listing.zeroResult]: {
        isLazyLoad: true
      }
    };
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const constraintsByContainerId = t9;
  let t10;
  let t11;
  if ($[22] !== adItems || $[23] !== isModalOpened) {
    t10 = () => {
      const isItemsListChanged = previousItemsList.current !== undefined && adItems != previousItemsList.current;
      if (!isFirstRender.current && isItemsListChanged && !isModalOpened) {
        setClientSideNavigationStarted(true);
        window.document.body.style.removeProperty("background-image");
      }
      previousItemsList.current = adItems;
      isFirstRender.current = false;
    };
    t11 = [adItems, isModalOpened];
    $[22] = adItems;
    $[23] = isModalOpened;
    $[24] = t10;
    $[25] = t11;
  } else {
    t10 = $[24];
    t11 = $[25];
  }
  useEffect(t10, t11);
  let t12;
  if ($[26] !== fallbackConfigDesktop || $[27] !== fallbackConfigMsite) {
    t12 = {
      msite: fallbackConfigMsite,
      desktop: fallbackConfigDesktop
    };
    $[26] = fallbackConfigDesktop;
    $[27] = fallbackConfigMsite;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== queryStringForAdsense) {
    t13 = {
      adtest,
      query: queryStringForAdsense
    };
    $[29] = queryStringForAdsense;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setClientSideNavigationStarted(false);
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== clientSideNavigationStarted || $[33] !== t12 || $[34] !== t13 || $[35] !== targeting || $[36] !== vertical) {
    t15 = <LiraRunAdvScript vertical={vertical} pageType="listing" env={process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT} fallbackHebeConfig={t12} adsense={t13} constraintsByContainerId={constraintsByContainerId} targeting={targeting} clientSideNavigationStarted={clientSideNavigationStarted} onAfterReloaded={t14} />;
    $[32] = clientSideNavigationStarted;
    $[33] = t12;
    $[34] = t13;
    $[35] = targeting;
    $[36] = vertical;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
};
export const LiraRunAdvScriptWrapperForListing = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ErrorBoundary><LiraWrapper {...props} /></ErrorBoundary>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(ad) {
  return ad.features["/transaction_status"];
}